<script setup lang="ts">
import { requiredValidator } from "@/@core/utils/validators";
import type { ActivitiesRecord } from "@/utils/PocketBaseAdapter";
import { pb } from "@/utils/PocketBaseAdapter";

// interface
interface Props {
  isOpen: boolean;
  activity?: ActivitiesRecord;
  isRecurring?: boolean;
  type?: string;
  isAdmin?: boolean;
  booking?: any,
  ticketCount?: number;
}

// props
const props = defineProps<Props>();

// Emit
const emit = defineEmits<{
  (e: "closeDialog", val: boolean): void;
  (e: "cancelBooking", val: boolean): void;
}>();

// data
const cancelAgreement = ref<boolean>(false);
const refVForm = ref<any>("");

// computed
const formattedCancelAgreement = computed(() => {
  if(props?.activity?.cancelAgreement) {
    return props?.activity?.cancelAgreement?.replace(
      /\[(.*?)\]\((.*?)\)/g,
      '<a href="$2" target="_blank">$1</a>'
    );
  } else if (props.booking?.expand?.activity?.cancelAgreement){
    return props.booking?.expand?.activity?.cancelAgreement.replace(
      /\[(.*?)\]\((.*?)\)/g,
      '<a href="$2" target="_blank">$1</a>'
    );
  }
});

// Method
const validateCancel = async (all: boolean = false) => {
  const { valid } = await refVForm.value.validate();
  if (valid) {

    // Add Reason to Booking Notes
    if(props?.booking?.id && deleteReason.value !== '') {
      await pb.collection("bookings").update(props?.booking?.id, {deletedReason: deleteReason.value})
    } else if(props?.booking?.id) {
      await pb.collection("bookings").update(props?.booking?.id, {deletedReason: "User Cancelled, no reason given"})
    }

    emit("cancelBooking", all);
  }
};

watch(
  () => [props?.isOpen],
  ([newValue]) => {
    if (newValue && props.isAdmin) {
      cancelAgreement.value = true;
    }
  },
  { immediate: true }
);

// watch
watchEffect(() => {
  if (!props?.isOpen) {
    cancelAgreement.value = false;
  }
});

const deleteReason = ref('');
</script>

<template>
  <VDialog :model-value="isOpen" persistent no-click-animation max-width="490">
    <VCard>
      <div class="d-flex align-center justify-space-between bg-error">
        <!-- 👉 Header -->
        <VCardTitle style="color: white;">Remove booking</VCardTitle>
        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="emit('closeDialog', false)"
        />
      </div>
      <VCardText>
        <div class="mb-5">
          Are you sure you want to remove this
          <span v-if="type==='Event'"> Event </span>
          <span v-else-if="props?.activity?.name"> {{props?.activity?.name}} </span> booking?
          <div v-if="props?.ticketCount"> This event has <strong>{{props?.ticketCount}}</strong> ticket(s) sold.</div>
        </div>

        <v-textarea label="Delete Reason" v-if="props?.booking?.state == 'Confirmed'" v-model="deleteReason">

        </v-textarea>

        <VForm ref="refVForm">
          <VCheckbox
            v-if="type !== 'Event'"
            v-model="cancelAgreement"
            :rules="[requiredValidator]"
          >
            <template #label>
              <div v-html="formattedCancelAgreement" />
            </template>
          </VCheckbox>
        </VForm>
      </VCardText>
      <VCardActions>
        <v-spacer></v-spacer>
        <VBtn
          color="grey"
          variant="outlined"
          @click="emit('closeDialog', false)"
        >
          No - Keep
        </VBtn>

        <!-- Conditionally show options if it's a recurring booking -->
        <template v-if="props?.isRecurring">
          <VBtn
            color="error"
            variant="flat"
            @click="validateCancel(false)"
          >
            Yes - Remove this one
          </VBtn>
          <VBtn color="error" variant="flat" @click="validateCancel(true)">
            Yes - Remove all
          </VBtn>
        </template>

        <!-- Show only single delete option for non-recurring bookings -->
        <template v-else>
          <VBtn color="error" variant="flat" @click="validateCancel">
            Yes - Remove
          </VBtn>
        </template>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
